import React, {useContext, useState} from 'react';

import classNames from 'classnames';

import {Button, IButtonProps} from './../button';
import {Card, CardContent} from '../card';
import {Grid, GridColumn} from '../grid';
import {IDateTime} from '../helpers';
import {IProgressProps, Progress} from './../progress';
import {FeedbackAnswers, IFeedbackAnswersProps} from '../feedback-answers';
import {Dropdown} from '../dropdown';
import {StoreContext} from '../../../app/src/base/root-store';
import {getTooltipButton} from '../button/button';
import {DistributionCounts} from '../dashboard-overview-card/dashboard-overview-card';

export interface IQuestionProps {
    bars: IProgressProps[];
    category: string;
    questionId?: string;
    text: string;
    time?: IDateTime;
    actions?: IButtonProps[];
    className?: string;
    answers?: IFeedbackAnswersProps;
    hasMentor?: IButtonProps;
    titleTooltip?: string | JSX.Element;
    distributionCounts?: DistributionCounts
}

const Question = (props: IQuestionProps): JSX.Element => {
    const store = useContext(StoreContext).teamResultsStore;
    const handleHasSeenToggle = (answerId: string, checked: boolean) => {
        store.managerChecksComment(answerId, checked);
    };
    const score =
        props.distributionCounts === undefined
            ? props.bars[0]?.percent ?? 0
            : props.bars[1]?.percent ?? 0;

    const [activeTab, setActiveTab] = useState<'results' | 'feedback'>('results');

    const className: string = classNames(
        'question',
        props.className,
    );

    const borderClass =
        score > 50
            ? 'score-high'
            : 'score-low';

    return (
        <div className={className}>
            <Card className={classNames('question__card', borderClass)}>
                <CardContent>
                    <div className="text">
                        <Grid align={['left-xs']} gutter='none'>
                            <GridColumn width={['max-xs']}>
                                <p>
                                    <span className="question__category text-small">{props.category}</span>
                                    {props.time ? <time className="question__time text-small"
                                        dateTime={props.time.iso}>{props.time.value}</time> : undefined
                                    }
                                    <span className="question__text">{props.text}</span>
                                </p>
                            </GridColumn>
                            <GridColumn width={['min-xs']} className="question__icon-container">
                                {getTooltipButton({
                                    icon: 'search',
                                    tooltip: store.label('dashboard.question-card.filter'),
                                    text: '',
                                    color: 'white',
                                    onClick: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                                        event.preventDefault();
                                        store.onQuestionOptionChange(String(props.questionId));
                                        store.toggleFilters();
                                    },
                                })}
                                {props.answers?.answers.length !== 0 && (
                                    <>
                                        {getTooltipButton({
                                            icon: activeTab === 'results' ? 'message' : 'bar-graph',
                                            tooltip: activeTab === 'results'
                                                ? store.label('dashboard.question-card.see-comments')
                                                : store.label('dashboard.question-card.see-results'),
                                            text: '',
                                            color: 'white',
                                            onClick: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                                                event.preventDefault();
                                                setActiveTab(activeTab === 'feedback' ? 'results' : 'feedback');
                                            },
                                            ...(activeTab === 'results'
                                                ? {
                                                    count: {
                                                        number: props.answers?.answers?.length ?? 0,
                                                        color: 'red',
                                                        size: 'sm',
                                                    },
                                                }
                                                : {}),
                                        })}
                                    </>
                                )}
                            </GridColumn>
                        </Grid>
                        <Grid>
                            <GridColumn width={['max-xs']} className="question__feedback-content">
                                {activeTab === 'results' && (
                                    <Grid noWrap={['sm']} className="question__results">
                                        <GridColumn width={['min-xs']} className="question__results-left">
                                            <Grid wrap={['xs']} >
                                                {props.bars.map((bar: IProgressProps, i: number) => (
                                                    <GridColumn width={['xs']} key={i}>
                                                        <Progress {...bar} />
                                                    </GridColumn>
                                                ))}
                                            </Grid>
                                            {props.hasMentor && (
                                                <div className="question__mentor">
                                                    {props.titleTooltip ? (
                                                        <Dropdown
                                                            delayShow={200}
                                                            placement={'top'}
                                                            trigger={{
                                                                children: (
                                                                    <Button
                                                                        {...props.hasMentor}
                                                                        className="button button--block button--color-green-border"
                                                                        iconLeft="robot-happy"
                                                                        color={'white'} style={'filter'}
                                                                    />
                                                                ),
                                                            }}
                                                        >
                                                            {props.titleTooltip}
                                                        </Dropdown>
                                                    ) : (
                                                        <Button
                                                            {...props.hasMentor}
                                                            className="button button--block button--color-green-border"
                                                            iconLeft="robot-happy"
                                                            color={'white'} style={'filter'}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                        </GridColumn>
                                        {props.distributionCounts !== undefined && (
                                            <div className="question__vertical-bars">
                                                {((): JSX.Element[] => {
                                                    const values = [
                                                        { rating: 0, count: props.distributionCounts?.count0 ?? 0 },
                                                        { rating: 25, count: props.distributionCounts?.count25 ?? 0 },
                                                        { rating: 50, count: props.distributionCounts?.count50 ?? 0 },
                                                        { rating: 75, count: props.distributionCounts?.count75 ?? 0 },
                                                        { rating: 100, count: props.distributionCounts?.count100 ?? 0 },
                                                    ];

                                                    const total = values.reduce((sum, v) => sum + v.count, 0);

                                                    return values.map(({ rating, count }) => {
                                                        const heightPercent = total > 0 ? (count / total) * 100 : 0;

                                                        return (
                                                            <div key={rating} className="question__vertical-bar">
                                                                <span className="question__vertical-bar-count">{count}</span>
                                                                <div
                                                                    className="question__vertical-bar-fill"
                                                                    style={{ height: `${heightPercent}%` }}
                                                                />
                                                                <span className="question__vertical-bar-label">{rating}%</span>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        )}
                                    </Grid>
                                )}
                                {activeTab === 'feedback' && props.answers && (
                                    <FeedbackAnswers
                                        {...props.answers}
                                        includeLikesAndComments={false}
                                        onHasSeenToggle={handleHasSeenToggle}
                                    />
                                )}
                            </GridColumn>
                        </Grid>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Question;
